// capture-transition.js - 캡처 후 전환 애니메이션
// 코너 감지 → 클립 → 펴지면서 전체화면 확대

/**
 * 캡처 전환 애니메이션 관리
 */
export class CaptureTransition {
  constructor() {
    this.container = null;
    this.frozenCanvas = null;
    this.overlayCanvas = null;
    this.isAnimating = false;
  }

  /**
   * 전환 컨테이너 초기화
   */
  init() {
    // 이미 존재하면 재사용
    if (this.container) return;

    this.container = document.createElement('div');
    this.container.className = 'capture-transition-container';
    this.container.innerHTML = `
      <canvas class="capture-transition-frozen"></canvas>
      <canvas class="capture-transition-overlay"></canvas>
      <div class="capture-transition-clip"></div>
    `;

    // 스타일 주입
    if (!document.getElementById('capture-transition-styles')) {
      const style = document.createElement('style');
      style.id = 'capture-transition-styles';
      style.textContent = `
        .capture-transition-container {
          position: fixed;
          inset: 0;
          z-index: 50;
          background: #000;
          display: none;
        }
        .capture-transition-container.active {
          display: block;
        }
        .capture-transition-frozen {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        .capture-transition-overlay {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
        }
        .capture-transition-clip {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.7);
          transition: clip-path 0.4s ease-out, opacity 0.3s;
        }
        .capture-transition-clip.highlight {
          background: rgba(0, 0, 0, 0.6);
        }
        .capture-transition-document {
          position: absolute;
          overflow: hidden;
          transition: all 0.4s ease-out;
          transform-origin: center center;
        }
        .capture-transition-document canvas {
          width: 100%;
          height: 100%;
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(this.container);
    this.frozenCanvas = this.container.querySelector('.capture-transition-frozen');
    this.overlayCanvas = this.container.querySelector('.capture-transition-overlay');
  }

  /**
   * 비디오 프레임 프리즈 + 코너 하이라이트
   * @param {HTMLVideoElement} video
   * @param {Object} corners - {tl, tr, br, bl} 비디오 좌표
   * @param {HTMLCanvasElement} realtimeOverlay - 기존 오버레이 캔버스
   */
  freezeWithCorners(video, corners, realtimeOverlay) {
    this.init();

    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;

    // 프리즈 캔버스에 현재 비디오 프레임 그리기
    this.frozenCanvas.width = videoWidth;
    this.frozenCanvas.height = videoHeight;
    const ctx = this.frozenCanvas.getContext('2d');
    ctx.drawImage(video, 0, 0, videoWidth, videoHeight);

    // 컨테이너 크기 맞춤
    const containerRect = video.getBoundingClientRect();
    this.container.style.top = containerRect.top + 'px';
    this.container.style.left = containerRect.left + 'px';
    this.container.style.width = containerRect.width + 'px';
    this.container.style.height = containerRect.height + 'px';
    this.container.style.inset = 'auto';

    // 오버레이 캔버스 크기
    this.overlayCanvas.width = containerRect.width;
    this.overlayCanvas.height = containerRect.height;

    // 비디오 좌표 → 화면 좌표 변환
    const transform = this.getVideoToScreenTransform(video, containerRect);
    const screenCorners = {
      tl: this.transformPoint(corners.tl, transform),
      tr: this.transformPoint(corners.tr, transform),
      br: this.transformPoint(corners.br, transform),
      bl: this.transformPoint(corners.bl, transform)
    };

    // 클립 영역 설정 (코너 바깥을 어둡게)
    this.applyClipMask(screenCorners, containerRect);

    // 코너 안쪽 밝게 하이라이트
    this.drawCornerHighlight(screenCorners);

    // 활성화
    this.container.classList.add('active');
    this.isAnimating = true;

    return { screenCorners, transform, containerRect };
  }

  /**
   * 비디오 → 화면 좌표 변환 정보
   */
  getVideoToScreenTransform(video, containerRect) {
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    const videoRatio = videoWidth / videoHeight;
    const containerRatio = containerWidth / containerHeight;

    let renderedWidth, renderedHeight, offsetX, offsetY;

    if (videoRatio > containerRatio) {
      renderedWidth = containerWidth;
      renderedHeight = containerWidth / videoRatio;
      offsetX = 0;
      offsetY = (containerHeight - renderedHeight) / 2;
    } else {
      renderedHeight = containerHeight;
      renderedWidth = containerHeight * videoRatio;
      offsetX = (containerWidth - renderedWidth) / 2;
      offsetY = 0;
    }

    return {
      scaleX: renderedWidth / videoWidth,
      scaleY: renderedHeight / videoHeight,
      offsetX,
      offsetY
    };
  }

  /**
   * 좌표 변환
   */
  transformPoint(point, transform) {
    return {
      x: point.x * transform.scaleX + transform.offsetX,
      y: point.y * transform.scaleY + transform.offsetY
    };
  }

  /**
   * 클립 마스크 적용 (코너 바깥 어둡게)
   */
  applyClipMask(corners, containerRect) {
    const clipEl = this.container.querySelector('.capture-transition-clip');

    // polygon으로 코너 안쪽을 투명하게 (반전 클립)
    const { tl, tr, br, bl } = corners;
    const w = containerRect.width;
    const h = containerRect.height;

    // 바깥 사각형에서 안쪽 사각형을 뺀 영역
    clipEl.style.clipPath = `polygon(
      0 0, ${w}px 0, ${w}px ${h}px, 0 ${h}px, 0 0,
      ${tl.x}px ${tl.y}px, ${bl.x}px ${bl.y}px, ${br.x}px ${br.y}px, ${tr.x}px ${tr.y}px, ${tl.x}px ${tl.y}px
    )`;
    clipEl.classList.add('highlight');
  }

  /**
   * 코너 안쪽 하이라이트 그리기
   */
  drawCornerHighlight(corners) {
    const ctx = this.overlayCanvas.getContext('2d');
    ctx.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);

    // 코너 테두리 (초록색)
    ctx.beginPath();
    ctx.moveTo(corners.tl.x, corners.tl.y);
    ctx.lineTo(corners.tr.x, corners.tr.y);
    ctx.lineTo(corners.br.x, corners.br.y);
    ctx.lineTo(corners.bl.x, corners.bl.y);
    ctx.closePath();

    ctx.strokeStyle = '#22c55e';
    ctx.lineWidth = 3;
    ctx.stroke();

    // 코너 점
    const radius = 8;
    ctx.fillStyle = '#22c55e';
    [corners.tl, corners.tr, corners.br, corners.bl].forEach(p => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  /**
   * 전체화면으로 확대 애니메이션
   * @param {Function} onComplete - 애니메이션 완료 콜백
   */
  async expandToFullscreen(onComplete) {
    if (!this.isAnimating) return;

    // 컨테이너를 전체화면으로 확대
    this.container.style.transition = 'all 0.4s ease-out';
    this.container.style.top = '0';
    this.container.style.left = '0';
    this.container.style.width = '100vw';
    this.container.style.height = '100vh';

    // 클립 마스크를 전체 사각형으로 (펴지는 효과)
    const clipEl = this.container.querySelector('.capture-transition-clip');
    clipEl.style.clipPath = 'none';
    clipEl.style.opacity = '0';

    // 오버레이도 페이드아웃
    this.overlayCanvas.style.transition = 'opacity 0.3s';
    this.overlayCanvas.style.opacity = '0';

    // 애니메이션 완료 대기
    await new Promise(resolve => setTimeout(resolve, 400));

    if (onComplete) {
      onComplete();
    }
  }

  /**
   * 정리 및 숨기기
   */
  hide() {
    if (this.container) {
      this.container.classList.remove('active');
      this.container.style.transition = '';

      const clipEl = this.container.querySelector('.capture-transition-clip');
      if (clipEl) {
        clipEl.style.clipPath = '';
        clipEl.style.opacity = '';
        clipEl.classList.remove('highlight');
      }

      if (this.overlayCanvas) {
        this.overlayCanvas.style.opacity = '';
      }
    }
    this.isAnimating = false;
  }

  /**
   * 리소스 정리
   */
  destroy() {
    this.hide();
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    this.container = null;
    this.frozenCanvas = null;
    this.overlayCanvas = null;
  }
}

// 싱글톤 인스턴스
export const captureTransition = new CaptureTransition();
